import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { TranscriptSocketEvent } from '@trinity/chat';
import { useFetchTranscriptSummary } from '@/utils/api';
import { useTranscriptsSocket } from '../../../../../packages/chat-components/src/websocket/hooks/useTranscriptsSocket';
import { RefreshSummariesContext } from './context';

type Props =
  ITranscriptId &
  ChildrenProps;

type Key = {
  transcriptId: number;
  tagId: number;
};

export const TranscriptSummaryRefreshContainer = (props: Props) => {

  const [websocketKey, setWebsocketKey] = useState<Key>();

  const apiClient = useChatApiClient();

  const {
    isPending: isGenerating,
    mutateAsync: refresh,
  } = useMutation({
    mutationKey: ['post:transcripts/conference-tags/summaries/generate', {
      transcriptId: props.transcriptId,
    }], mutationFn: (tagId?: number) => {
      return apiClient.transcriptsSummary.generateTagSummaries({
        transcriptId: props.transcriptId,
        tagIds: tagId ? [tagId] : undefined,
      });
    }, onMutate: (tagId?: number) => {
      setWebsocketKey({
        transcriptId: props.transcriptId,
        tagId,
      });
    }, onError: () => {
      setWebsocketKey(null);
    },
  });

  const { refetch: refreshTagSummaries, isLoading: isLoadingSummaries } = useFetchTranscriptSummary({
    transcriptId: props.transcriptId,
  });

  const handleTagSummariesAvailable = useCallback(() => {
    refreshTagSummaries();
    setWebsocketKey(null);
  }, [refreshTagSummaries]);

  const socket = useTranscriptsSocket(websocketKey);

  useEffect(() => {
    if (!socket) return;

    if (websocketKey) {
      socket.on(TranscriptSocketEvent.TagSummariesAvailable, handleTagSummariesAvailable);

      return () => {
        socket.off(TranscriptSocketEvent.TagSummariesAvailable, handleTagSummariesAvailable);
      };
    }
  }, [websocketKey, handleTagSummariesAvailable, socket]);

  const isRefreshing = useMemo(() => {
    return isGenerating || isLoadingSummaries || !!websocketKey;
  }, [
    isGenerating,
    isLoadingSummaries,
    websocketKey,
  ]);

  const ctx = useMemo(() => ({
    isRefreshing,
    refresh,
    tagRefreshing: websocketKey?.tagId,
  }), [
    isRefreshing,
    refresh,
    websocketKey,
  ]);

  return (
    <RefreshSummariesContext.Provider value={ctx}>
      {props.children}
    </RefreshSummariesContext.Provider>
  );
};